import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { useSetting } from '@/features/settings';
import { logbookEntries } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';

import {
  type CurrencyLevel,
  computeRecentExperience,
  evaluateExpiryReminders,
  RECENCY_WINDOW_DAYS,
} from './currency';
import type { ExpiryReminder } from './currency';
import {
  saveCurrencyReminders,
  useCurrencyReminders,
  useUpdateReminder,
} from './use-currency-reminders';

/**
 * Currency and expiry — `PLAN.md` §6 Phase 6, `NZ-LOGBOOK.md` §5. Every row
 * on this page is a reminder to check, never authority, so every one shows
 * the rule it is based on next to the number.
 */

const LEVEL_STYLE: Record<CurrencyLevel, string> = {
  ok: 'border-border text-muted-foreground',
  expiring: 'border-accent text-foreground bg-accent-soft',
  expired: 'border-danger text-danger-foreground bg-danger',
  unknown: 'border-border text-muted-foreground',
};

const LEVEL_TEXT: Record<CurrencyLevel, string> = {
  ok: 'Current',
  expiring: 'Expiring soon',
  expired: 'Expired',
  unknown: 'Not set',
};

function LevelBadge({ level }: { level: CurrencyLevel }) {
  return (
    <span
      className={`rounded-control inline-flex items-center border px-2 py-0.5 text-xs font-medium ${LEVEL_STYLE[level]}`}
    >
      {LEVEL_TEXT[level]}
    </span>
  );
}

const RECENT_EXPERIENCE_RULE =
  'CAR 61.37 — 3 takeoffs and landings in an aircraft of the same type or class, in the preceding 90 days. Day and night counted separately.';

const INPUT_CLASS =
  'border-border bg-surface rounded-control border px-2 py-1 text-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

export function CurrencyPage() {
  const entries = useLiveQuery(() => logbookEntries.listOrdered(), [], null);
  const [timeZone] = useSetting('timeZone');
  const reminders = useCurrencyReminders();
  const updateReminder = useUpdateReminder();
  const [editingId, setEditingId] = useState<string | null>(null);

  const asOf = formatLocalDate(new Date().toISOString(), { format: 'iso', timeZone });
  const recentExperience = useMemo(
    () => (entries === null ? null : computeRecentExperience(entries, asOf)),
    [entries, asOf],
  );
  const reminderStatuses = useMemo(
    () => (reminders === null ? [] : evaluateExpiryReminders(reminders, asOf)),
    [reminders, asOf],
  );

  if (entries === null || reminders === null || recentExperience === null) {
    return <p className="text-muted-foreground text-sm">Opening the local database…</p>;
  }

  const addReminder = () => {
    const next: ExpiryReminder = {
      id: crypto.randomUUID(),
      label: 'New reminder',
      ruleReference: '',
      expiryDate: null,
      warnWithinDays: 30,
    };
    void saveCurrencyReminders([...reminders, next]);
    setEditingId(next.id);
  };

  const removeReminder = (id: string) => {
    void saveCurrencyReminders(reminders.filter((reminder) => reminder.id !== id));
  };

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">As of {asOf}.</p>

      <section className="border-border bg-surface rounded-container border p-4">
        <h2 className="text-sm font-semibold tracking-tight">Recent experience</h2>
        <p className="text-muted-foreground mt-1 text-xs">
          Takeoffs and landings in the last {RECENCY_WINDOW_DAYS} days, by aircraft type.
        </p>
        {recentExperience.byType.length === 0 ? (
          <p className="text-muted-foreground mt-3 text-sm">
            No recent flights with an aircraft type.
          </p>
        ) : (
          <div className="mt-3 space-y-4">
            {recentExperience.byType.map((type) => (
              <div
                key={type.aircraftType}
                className="border-border border-b pb-4 last:border-0 last:pb-0"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold tracking-tight">{type.aircraftType}</h3>
                  <span className="text-muted-foreground text-xs">Last flown {type.lastFlown}</span>
                </div>
                <div className="mt-2 space-y-2">
                  {type.statuses.map((status) => (
                    <div key={status.id} className="flex flex-wrap items-center gap-2">
                      <span className="text-sm">{status.label}</span>
                      <LevelBadge level={status.level} />
                      <span className="text-muted-foreground text-xs">
                        {status.count} of {status.required} required.
                        {status.lapsesOn === undefined ? '' : ` Current until ${status.lapsesOn}.`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {recentExperience.untyped.entryCount === 0 ? null : (
          <p className="text-muted-foreground mt-3 text-xs">
            {recentExperience.untyped.entryCount}{' '}
            {recentExperience.untyped.entryCount === 1 ? 'entry has' : 'entries have'} landings but
            no aircraft type. Not counted.
          </p>
        )}
        <p className="text-muted-foreground mt-3 text-xs italic">{RECENT_EXPERIENCE_RULE}</p>
      </section>

      {/*
        A list, edited a row at a time. Every reminder used to be an open form —
        label, rule, date and warning inputs, four times over — so the page
        read as paperwork to fill in rather than as the answer to "am I
        current?". The status is the row; the inputs are behind Edit.
      */}
      <section className="border-border bg-surface rounded-container border p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold tracking-tight">Dates to watch</h2>
          <Button size="sm" variant="outline" onClick={addReminder}>
            <PlusIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Add reminder
          </Button>
        </div>
        <ul className="divide-border mt-3 divide-y">
          {reminderStatuses.map(({ reminder, level, daysRemaining }) => (
            <li key={reminder.id} className="py-2.5 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{reminder.label}</p>
                  {reminder.ruleReference === '' ? null : (
                    <p
                      className="text-muted-foreground truncate text-xs"
                      title={reminder.ruleReference}
                    >
                      {reminder.ruleReference}
                    </p>
                  )}
                </div>
                <span className="tabular text-sm">{reminder.expiryDate ?? '—'}</span>
                <span className="text-muted-foreground w-28 text-xs">
                  {daysRemaining === null
                    ? ''
                    : daysRemaining >= 0
                      ? `${String(daysRemaining)} days remaining`
                      : `${String(Math.abs(daysRemaining))} days overdue`}
                </span>
                <LevelBadge level={level} />
                <Button
                  size="sm"
                  variant="ghost"
                  aria-expanded={editingId === reminder.id}
                  onClick={() => {
                    setEditingId((current) => (current === reminder.id ? null : reminder.id));
                  }}
                >
                  {editingId === reminder.id ? 'Done' : 'Edit'}
                </Button>
              </div>
              {editingId === reminder.id ? (
                <div className="bg-surface-sunken rounded-control mt-2 space-y-2 p-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="space-y-1 text-xs">
                      <span className="text-muted-foreground">Name</span>
                      <input
                        type="text"
                        value={reminder.label}
                        onChange={(e) => {
                          updateReminder(reminder.id, { label: e.target.value });
                        }}
                        className={`${INPUT_CLASS} w-full`}
                      />
                    </label>
                    <label className="space-y-1 text-xs">
                      <span className="text-muted-foreground">Rule</span>
                      <input
                        type="text"
                        value={reminder.ruleReference}
                        onChange={(e) => {
                          updateReminder(reminder.id, { ruleReference: e.target.value });
                        }}
                        className={`${INPUT_CLASS} w-full`}
                      />
                    </label>
                  </div>
                  <div className="flex flex-wrap items-end gap-3 text-xs">
                    <label className="space-y-1">
                      <span className="text-muted-foreground block">Expiry date</span>
                      <input
                        type="date"
                        value={reminder.expiryDate ?? ''}
                        onChange={(e) => {
                          updateReminder(reminder.id, {
                            ...(e.target.value === ''
                              ? { expiryDate: null }
                              : { expiryDate: e.target.value }),
                          });
                        }}
                        className={INPUT_CLASS}
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-muted-foreground block">Warn within (days)</span>
                      <input
                        type="number"
                        min={0}
                        value={reminder.warnWithinDays}
                        onChange={(e) => {
                          const parsed = Number.parseInt(e.target.value, 10);
                          updateReminder(reminder.id, {
                            warnWithinDays: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
                          });
                        }}
                        className={`${INPUT_CLASS} w-20`}
                      />
                    </label>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => {
                        removeReminder(reminder.id);
                      }}
                    >
                      <TrashIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {reminderStatuses.length === 0 ? (
          <p className="text-muted-foreground text-sm">No date-based reminders.</p>
        ) : null}
      </section>
    </div>
  );
}
